import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

const STROKE_COLORS = [
  { id: '#FFD700', label: 'Gold' },
  { id: '#FF6B35', label: 'Orange' },
  { id: '#DC3545', label: 'Red' },
  { id: '#28A745', label: 'Green' },
  { id: '#007BFF', label: 'Blue' },
  { id: '#FFFFFF', label: 'White' },
  { id: '#E83E8C', label: 'Pink' },
  { id: '#20C997', label: 'Teal' },
];

interface Property {
  id: string;
  name: string;
  description: string | null;
  boundary: Array<{ lat: number; lon: number }>;
  strokeColor: string | null;
  acreage: string | null;
  legalDescription: string | null;
}

interface BoundaryPoint {
  lat: string;
  lon: string;
}

type PanelMode =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; property: Property }
  | { type: 'view'; property: Property };

// ---------------------------------------------------------------------------
// Boundary Renderer (lines only, no fill)
// ---------------------------------------------------------------------------

function PropertyBoundaries({ properties, activeId }: {
  properties: Property[]; activeId: string | null;
}) {
  const map = useMap();
  const overlaysRef = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (!map) return;
    overlaysRef.current.forEach((p) => p.setMap(null));
    overlaysRef.current = [];

    for (const prop of properties) {
      if (!prop.boundary || prop.boundary.length < 2) continue;
      const isActive = prop.id === activeId;
      const path = [...prop.boundary.map((p) => ({ lat: p.lat, lng: p.lon }))];
      if (path.length >= 3) path.push(path[0]);

      const polyline = new google.maps.Polyline({
        path,
        strokeColor: prop.strokeColor || '#FFD700',
        strokeWeight: isActive ? 4 : 2,
        strokeOpacity: isActive ? 1.0 : 0.7,
        map,
      });
      overlaysRef.current.push(polyline);
    }

    return () => { overlaysRef.current.forEach((p) => p.setMap(null)); };
  }, [map, properties, activeId]);

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PropertiesPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'list' });
  const [mapClickEnabled, setMapClickEnabled] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStrokeColor, setFormStrokeColor] = useState('#FFD700');
  const [formAcreage, setFormAcreage] = useState('');
  const [formLegalDesc, setFormLegalDesc] = useState('');
  const [formPoints, setFormPoints] = useState<BoundaryPoint[]>([]);

  const activeId = (panel.type === 'view' || panel.type === 'edit') ? (panel as any).property?.id : null;

  useEffect(() => { loadProperties(); }, []);

  async function loadProperties() {
    setLoading(true);
    const res = await apiFetch<Property[]>('/api/admin/properties');
    if (res.success && res.data) setProperties(res.data);
    setLoading(false);
  }

  // Map click adds a point when enabled
  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (!mapClickEnabled) return;
    if (!e.detail.latLng) return;
    setFormPoints((prev) => [...prev, {
      lat: e.detail.latLng!.lat.toFixed(7),
      lon: e.detail.latLng!.lng.toFixed(7),
    }]);
  }, [mapClickEnabled]);

  function openCreate() {
    setFormName('');
    setFormDescription('');
    setFormStrokeColor('#FFD700');
    setFormAcreage('');
    setFormLegalDesc('');
    setFormPoints([]);
    setMapClickEnabled(true);
    setPanel({ type: 'create' });
  }

  function openEdit(prop: Property) {
    setFormName(prop.name);
    setFormDescription(prop.description || '');
    setFormStrokeColor(prop.strokeColor || '#FFD700');
    setFormAcreage(prop.acreage || '');
    setFormLegalDesc(prop.legalDescription || '');
    setFormPoints(prop.boundary.map((p) => ({ lat: String(p.lat), lon: String(p.lon) })));
    setMapClickEnabled(true);
    setPanel({ type: 'edit', property: prop });
  }

  function cancelForm() {
    setMapClickEnabled(false);
    setPanel({ type: 'list' });
  }

  function addEmptyPoint() {
    setFormPoints((prev) => [...prev, { lat: '', lon: '' }]);
  }

  function updatePoint(index: number, field: 'lat' | 'lon', value: string) {
    setFormPoints((prev) => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  }

  function removePoint(index: number) {
    setFormPoints((prev) => prev.filter((_, i) => i !== index));
  }

  async function saveProperty() {
    const validPoints = formPoints
      .filter((p) => p.lat && p.lon && !isNaN(parseFloat(p.lat)) && !isNaN(parseFloat(p.lon)))
      .map((p) => ({ lat: parseFloat(p.lat), lon: parseFloat(p.lon) }));

    if (validPoints.length < 3) {
      alert('At least 3 valid boundary points are required.');
      return;
    }
    if (!formName.trim()) {
      alert('Property name is required.');
      return;
    }

    const payload = {
      name: formName,
      description: formDescription || undefined,
      boundary: validPoints,
      stroke_color: formStrokeColor,
      acreage: formAcreage ? parseFloat(formAcreage) : undefined,
      legal_description: formLegalDesc || undefined,
    };

    if (panel.type === 'edit') {
      await apiFetch(`/api/admin/properties/${(panel as any).property.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    } else {
      await apiFetch('/api/admin/properties', { method: 'POST', body: JSON.stringify(payload) });
    }
    setMapClickEnabled(false);
    setPanel({ type: 'list' });
    loadProperties();
  }

  async function deleteProperty(prop: Property) {
    if (!confirm(`Delete property "${prop.name}"?`)) return;
    await apiFetch(`/api/admin/properties/${prop.id}`, { method: 'DELETE' });
    setMapClickEnabled(false);
    setPanel({ type: 'list' });
    loadProperties();
  }

  function calcAreaAcres(points: Array<{ lat: number; lon: number }>): string {
    if (points.length < 3) return '0';
    const toRad = Math.PI / 180;
    const cLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const mPerDegLat = 111320;
    const mPerDegLon = 111320 * Math.cos(cLat * toRad);
    const pts = points.map((p) => ({ x: (p.lon - points[0].lon) * mPerDegLon, y: (p.lat - points[0].lat) * mPerDegLat }));
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      area += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    const sqm = Math.abs(area) / 2;
    const acres = sqm / 4046.86;
    return acres >= 1 ? `${acres.toFixed(1)} acres` : `${sqm.toFixed(0)} m²`;
  }

  // Preview points for the map (from form)
  const previewPoints = formPoints
    .filter((p) => p.lat && p.lon && !isNaN(parseFloat(p.lat)) && !isNaN(parseFloat(p.lon)))
    .map((p) => ({ lat: parseFloat(p.lat), lon: parseFloat(p.lon) }));

  const isEditing = panel.type === 'create' || panel.type === 'edit';

  return (
    <div style={st.page}>
      {/* LEFT: Map */}
      <div style={st.mapCol}>
        <div style={st.mapHeader}>
          <h1 style={st.pageTitle}>Properties</h1>
          {mapClickEnabled && <span style={st.mapHint}>Click the map to add boundary points</span>}
        </div>
        <div style={st.mapWrap}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              defaultCenter={PROPERTY_CENTER}
              defaultZoom={15}
              mapTypeId="satellite"
              gestureHandling="greedy"
              fullscreenControl={false}
              mapId="grimsthorpe-admin-properties"
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%', cursor: mapClickEnabled ? 'crosshair' : undefined }}
            >
              {/* Existing property vertex pins */}
              {!isEditing && properties.map((prop) =>
                prop.boundary?.length > 0 ? (
                  <AdvancedMarker
                    key={`pin-${prop.id}`}
                    position={{ lat: prop.boundary[0].lat, lng: prop.boundary[0].lon }}
                    title={prop.name}
                    onClick={() => setPanel({ type: 'view', property: prop })}
                  >
                    <Pin background={prop.strokeColor || '#FFD700'} borderColor="#000" glyphColor="#000" scale={0.8} />
                  </AdvancedMarker>
                ) : null
              )}

              {/* Preview markers for form points */}
              {isEditing && previewPoints.map((pt, i) => (
                <AdvancedMarker key={`preview-${i}`} position={{ lat: pt.lat, lng: pt.lon }}>
                  <Pin
                    background={i === 0 ? formStrokeColor : '#00BFFF'}
                    borderColor="#000"
                    glyphColor="#FFF"
                    scale={i === 0 ? 0.8 : 0.5}
                  />
                </AdvancedMarker>
              ))}

              {/* Info window */}
              {panel.type === 'view' && (panel as any).property.boundary?.length > 0 && (
                <InfoWindow
                  position={{ lat: (panel as any).property.boundary[0].lat, lng: (panel as any).property.boundary[0].lon }}
                  onCloseClick={() => setPanel({ type: 'list' })}
                  pixelOffset={[0, -40]}
                >
                  <div style={{ color: '#000', minWidth: 160 }}>
                    <strong style={{ fontSize: 14 }}>{(panel as any).property.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>
                      {(panel as any).property.boundary.length} pts &middot; {calcAreaAcres((panel as any).property.boundary)}
                    </p>
                    <button onClick={() => openEdit((panel as any).property)}
                      style={{ marginTop: 4, padding: '4px 12px', fontSize: 12, backgroundColor: '#1B3B1B', color: '#FFD700', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Edit Property
                    </button>
                  </div>
                </InfoWindow>
              )}

              {/* Preview boundary line */}
              {isEditing && <PreviewBoundary points={previewPoints} color={formStrokeColor} />}

              <PropertyBoundaries properties={isEditing ? [] : properties} activeId={activeId} />
            </GoogleMap>
          </APIProvider>
        </div>
      </div>

      {/* RIGHT: Panel */}
      <div style={st.panelCol}>
        <div style={st.panelContent}>

          {/* LIST */}
          {panel.type === 'list' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={st.panelTitle}>Properties</h3>
                <button style={st.btnPrimary} onClick={openCreate}>+ Add Property</button>
              </div>
              {loading ? <div style={{ color: COLORS.textMuted }}>Loading...</div> :
                properties.length === 0 ? (
                  <p style={{ color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' }}>No properties defined yet.</p>
                ) : properties.map((prop) => (
                  <div key={prop.id} style={st.itemRow} onClick={() => setPanel({ type: 'view', property: prop })}>
                    <div style={{ width: 12, height: 12, borderRadius: 2, border: `2px solid ${prop.strokeColor || '#FFD700'}`, flexShrink: 0 }} />
                    <div>
                      <div style={{ color: COLORS.textPrimary, fontSize: 13, fontWeight: 500 }}>{prop.name}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: 11 }}>
                        {prop.boundary?.length || 0} pts &middot; {calcAreaAcres(prop.boundary || [])}
                      </div>
                    </div>
                  </div>
                ))
              }
            </>
          )}

          {/* VIEW */}
          {panel.type === 'view' && (
            <>
              <h3 style={st.panelTitle}>{(panel as any).property.name}</h3>
              {(panel as any).property.description && (
                <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 8 }}>{(panel as any).property.description}</p>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ ...st.badge, backgroundColor: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>
                  {(panel as any).property.boundary?.length || 0} boundary points
                </span>
                <span style={{ ...st.badge, backgroundColor: 'rgba(255,255,255,0.05)', color: COLORS.textMuted }}>
                  {calcAreaAcres((panel as any).property.boundary || [])}
                </span>
              </div>
              {(panel as any).property.legalDescription && (
                <div style={{ padding: 10, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6, marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>Legal Description</div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary }}>{(panel as any).property.legalDescription}</div>
                </div>
              )}
              <div style={st.panelActions}>
                <button style={{ ...st.btnDanger, marginRight: 'auto' }} onClick={() => deleteProperty((panel as any).property)}>Delete</button>
                <button style={st.btnSecondary} onClick={() => setPanel({ type: 'list' })}>Close</button>
                <button style={st.btnPrimary} onClick={() => openEdit((panel as any).property)}>Edit</button>
              </div>
            </>
          )}

          {/* CREATE / EDIT */}
          {isEditing && (
            <>
              <h3 style={st.panelTitle}>{panel.type === 'edit' ? 'Edit Property' : 'New Property'}</h3>

              <div style={st.field}>
                <label style={st.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={st.input} autoFocus placeholder="e.g. Grimsthorpe Main Parcel" />
              </div>

              <div style={st.field}>
                <label style={st.label}>Description</label>
                <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2} style={{ ...st.input, resize: 'vertical' }} />
              </div>

              <div style={st.field}>
                <label style={st.label}>Boundary Color</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {STROKE_COLORS.map((c) => (
                    <button key={c.id} title={c.label} onClick={() => setFormStrokeColor(c.id)}
                      style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: c.id,
                        border: formStrokeColor === c.id ? '3px solid #FFF' : '2px solid #333',
                        cursor: 'pointer', outline: formStrokeColor === c.id ? `2px solid ${c.id}` : 'none' }} />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...st.field, flex: 1 }}>
                  <label style={st.label}>Acreage (stated)</label>
                  <input type="number" step="0.01" value={formAcreage} onChange={(e) => setFormAcreage(e.target.value)} style={st.input} placeholder="Optional" />
                </div>
              </div>

              <div style={st.field}>
                <label style={st.label}>Legal Description</label>
                <input value={formLegalDesc} onChange={(e) => setFormLegalDesc(e.target.value)} style={st.input} placeholder="Lot/Concession (optional)" />
              </div>

              {/* Boundary Points */}
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={st.label}>Boundary Points ({formPoints.length})</label>
                  <button style={st.btnSmall} onClick={addEmptyPoint}>+ Add Point</button>
                </div>

                <p style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 8 }}>
                  Click the map or enter coordinates below. Min 3 points.
                </p>

                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                  {formPoints.map((pt, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ color: COLORS.textMuted, fontSize: 10, width: 18, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
                      <input
                        value={pt.lat}
                        onChange={(e) => updatePoint(i, 'lat', e.target.value)}
                        placeholder="Lat"
                        style={{ ...st.input, flex: 1, padding: '4px 6px', fontSize: 11 }}
                      />
                      <input
                        value={pt.lon}
                        onChange={(e) => updatePoint(i, 'lon', e.target.value)}
                        placeholder="Lon"
                        style={{ ...st.input, flex: 1, padding: '4px 6px', fontSize: 11 }}
                      />
                      <button onClick={() => removePoint(i)} style={st.btnRemove}>&times;</button>
                    </div>
                  ))}
                </div>

                {previewPoints.length >= 3 && (
                  <div style={{ marginTop: 8, padding: 8, backgroundColor: COLORS.backgroundTertiary, borderRadius: 4 }}>
                    <span style={{ color: COLORS.textSecondary, fontSize: 11 }}>
                      Calculated area: <strong>{calcAreaAcres(previewPoints)}</strong>
                    </span>
                  </div>
                )}
              </div>

              <div style={st.panelActions}>
                {panel.type === 'edit' && (
                  <button style={{ ...st.btnDanger, marginRight: 'auto' }} onClick={() => deleteProperty((panel as any).property)}>Delete</button>
                )}
                <button style={st.btnSecondary} onClick={cancelForm}>Cancel</button>
                <button style={st.btnPrimary} onClick={saveProperty} disabled={!formName.trim() || previewPoints.length < 3}>
                  {panel.type === 'edit' ? 'Update' : 'Save Property'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview boundary line component
// ---------------------------------------------------------------------------

function PreviewBoundary({ points, color }: { points: Array<{ lat: number; lon: number }>; color: string }) {
  const map = useMap();
  const lineRef = useRef<google.maps.Polyline | null>(null);

  useEffect(() => {
    if (!map) return;
    if (lineRef.current) lineRef.current.setMap(null);

    if (points.length < 2) { lineRef.current = null; return; }

    const path = [...points.map((p) => ({ lat: p.lat, lng: p.lon }))];
    if (path.length >= 3) path.push(path[0]);

    lineRef.current = new google.maps.Polyline({
      path,
      strokeColor: color,
      strokeWeight: 3,
      strokeOpacity: 0.9,
      map,
    });

    return () => { if (lineRef.current) lineRef.current.setMap(null); };
  }, [map, points, color]);

  return null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const st: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  mapHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', flexShrink: 0 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  mapHint: { fontSize: 12, color: '#00BFFF', fontWeight: 500 },
  mapWrap: { flex: 1, borderRadius: 8, overflow: 'hidden', margin: '0 0 0 16px' },
  panelCol: { width: 400, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, flexWrap: 'wrap' },
  itemRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', marginBottom: 4, borderRadius: 6, border: `1px solid ${COLORS.borderDefault}`, cursor: 'pointer' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSmall: { padding: '5px 10px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' },
  btnRemove: { width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 4, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
};
